import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { TotalsByPeriodChart } from '@/components/charts/TotalsByPeriodChart';
import { formatMoney } from '@/lib/currency';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

/**
 * No-signup demo. Everything is seeded client-side from a static fixture
 * so the page works without backend, OAuth, Postgres, or CadencEZ AI. The
 * point is to prove the *feel* of the product in under 30 seconds.
 */

const PERIODS = ['2026-01-01','2026-02-01','2026-03-01','2026-04-01','2026-05-01','2026-06-01','2026-07-01','2026-08-01'];
const BUDGET = [82, 85, 88, 91, 94, 97, 100, 103];
const ACTUAL = [80, 87, 90, 95, 96, 102, 108, 116];

const VARIANCES = [
  { account: '4100 · EU OPEX',     budget: 39_500_000, actual: 48_200_000, pct: 22.0, ai: 'Travel costs in EU [fact:12384] rose ¥6.2M after the Berlin office expansion in Q2 — a one-off renovation payment to vendor 3 [fact:12390] accounts for ¥4.1M. Recurring OPEX up ¥2.0M from headcount. Driver: one-off' },
  { account: '5200 · APAC sales',  budget: 128_000_000, actual: 146_500_000, pct: 14.4, ai: 'APAC sales overshot driven by Q2 acceleration in JP enterprise tier [fact:14820]. New logos from Mercari [fact:14831] and GMO [fact:14844] account for ¥11.2M; expansion within existing accounts contributed ¥7.3M. Driver: volume' },
  { account: '7100 · Headcount',   budget: 92_000_000, actual: 99_100_000, pct: 7.7, ai: 'Headcount overrun is +5 FTE earlier than plan, concentrated in engineering [fact:15201..15205]. No bonus accrual variance. Driver: timing' },
  { account: '8100 · Marketing',   budget: 22_000_000, actual: 14_800_000, pct: -32.7, ai: 'Marketing underspend: delayed JP brand campaign [fact:16002] (¥6.4M pushed to Q3) and Berlin sponsor budget not used [fact:16018]. Driver: timing' },
];

const SERIES = [
  { name: 'budget', data: PERIODS.map((p, i) => ({ x: p, y: BUDGET[i] * 1_000_000 })) },
  { name: 'actual', data: PERIODS.map((p, i) => ({ x: p, y: ACTUAL[i] * 1_000_000 })) },
];

export function DemoPage() {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<number | null>(0);
  useDocumentMeta({
    title: 'Live demo — CadencEZ',
    description: 'No-signup CadencEZ demo: budget vs actual, variance analysis, AI-cited explanations. Seeded client-side, runs in 30 seconds.',
    path: '/demo',
  });
  return (
    <div className="min-h-screen bg-ink-50">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="inline-flex text-[10px] uppercase tracking-wide font-semibold text-accent-700 bg-accent-50 rounded-md px-2 py-0.5 mb-2">{t('demo.badge')}</div>
            <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">Acme K.K. · FY2026</h1>
            <p className="mt-1 text-ink-600">{t('hero.sub').slice(0, 80)}…</p>
          </div>
          <Link
            to={`/`}
            className="px-4 py-2 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 transition shadow-soft"
          >
            {t('hero.primary_cta')}
          </Link>
        </div>

        <Tile title={t('workspace.totals_title')}>
          <TotalsByPeriodChart series={SERIES} />
        </Tile>

        <Tile title={t('variance.title')}>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100">
                <th className="py-2 pr-3 font-medium">{t('variance.th_account')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_budget')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_actual')}</th>
                <th className="py-2 px-3 font-medium text-right">{t('variance.th_pct')}</th>
                <th className="py-2 px-3 font-medium">{t('variance.th_explanation')}</th>
              </tr>
            </thead>
            <tbody>
              {VARIANCES.map((v, i) => (
                <>
                  <tr key={`r-${i}`} className="border-b border-ink-50 align-top">
                    <td className="py-2 pr-3 num font-medium text-ink-900">{v.account}</td>
                    <td className="py-2 px-3 num text-right">{formatMoney(v.budget, { currency: 'JPY', maximumFractionDigits: 0 })}</td>
                    <td className="py-2 px-3 num text-right">{formatMoney(v.actual, { currency: 'JPY', maximumFractionDigits: 0 })}</td>
                    <td className={`py-2 px-3 num text-right ${Math.abs(v.pct) >= 20 ? 'text-warn-600 font-semibold' : 'text-ink-700'}`}>{v.pct.toFixed(1)}%</td>
                    <td className="py-2 px-3">
                      <button onClick={() => setOpenId((cur) => (cur === i ? null : i))} className="text-accent-600 hover:underline text-xs font-medium">
                        {openId === i ? t('variance.hide') : t('variance.view')}
                      </button>
                    </td>
                  </tr>
                  {openId === i && (
                    <tr key={`e-${i}`}>
                      <td colSpan={5} className="px-3 pb-4">
                        <div className="rounded-xl bg-ink-50 border border-ink-200 p-4">
                          <div className="text-xs text-emerald-700 font-semibold mb-1">✦ {t('variance.ai_byline')}</div>
                          <p className="text-sm text-ink-800 leading-relaxed">{v.ai}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
          </div>
        </Tile>

        <div className="rounded-lg bg-foreground text-background px-6 py-8 text-center">
          <h3 className="display text-2xl font-medium tracking-tight">{t('cta.headline')}</h3>
          <p className="mt-1 text-background/65 text-sm">{t('hero.no_card')}</p>
          <Link
            to="/signup"
            className="mt-4 inline-flex items-center px-5 py-2.5 rounded-md bg-emerald-500 text-foreground text-sm font-semibold hover:bg-emerald-400 transition-colors"
          >
            {t('nav.signup')}
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Tile({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-ink-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}
